import { useTranslation } from 'react-i18next';
import { Column, ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import ArrowDownIconSolid from '@/components/icons/ArrowDownIconSolid';
import EDateText from '@/components/everridge/EDateText';
import { UserActivityListItemDto, UserActivitySource } from '../types/superAdmin-userActivities-dto';
import { useUserActivitiesFilter } from '../hooks/useUserActivitiesFilter';

const useUserActivitiesPageColumns = (): ColumnDef<UserActivityListItemDto>[] => {
    const { t } = useTranslation();
    const { setFilter } = useUserActivitiesFilter();

    const handleSort = (
        apiOrderBy: 'Date' | 'User' | 'Source' | 'Type',
        column: Column<UserActivityListItemDto>,
    ) => {
        const newDirection = column.getIsSorted() === 'asc';
        column.toggleSorting(newDirection);

        setFilter({
            orderBy: apiOrderBy,
            orderDirection: newDirection ? 'asc' : 'desc',
            skip: 0,
        });
    };

    const getSourceLabel = (source: UserActivitySource) => {
        if (source === UserActivitySource.App) {
            return t('main.superAdmin.userActivities.source.app');
        }
        return t('main.superAdmin.userActivities.source.keycloak');
    };

    return [
        {
            accessorKey: 'date',
            header: ({ column }) => (
                <Button
                    variant="link"
                    className="h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline"
                    onClick={() => handleSort('Date', column)}
                >
                    {t('main.superAdmin.userActivities.table.header.date')}
                    <div
                        className={`transition-transform duration-50 ease-in-out ${
                            column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                        }`}
                    >
                        <ArrowDownIconSolid />
                    </div>
                </Button>
            ),
            cell: ({ row }) => (
                <div className="text-sm text-popover-800">
                    <EDateText date={row.getValue('date')} />
                </div>
            ),
        },
        {
            accessorKey: 'source',
            header: ({ column }) => (
                <Button
                    variant="link"
                    className="h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline"
                    onClick={() => handleSort('Source', column)}
                >
                    {t('main.superAdmin.userActivities.table.header.source')}
                    <div
                        className={`transition-transform duration-50 ease-in-out ${
                            column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                        }`}
                    >
                        <ArrowDownIconSolid />
                    </div>
                </Button>
            ),
            cell: ({ row }) => (
                <div className="text-sm text-popover-800">
                    {getSourceLabel(row.getValue('source'))}
                </div>
            ),
        },
        {
            accessorKey: 'eventType',
            header: ({ column }) => (
                <Button
                    variant="link"
                    className="h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline"
                    onClick={() => handleSort('Type', column)}
                >
                    {t('main.superAdmin.userActivities.table.header.eventType')}
                    <div
                        className={`transition-transform duration-50 ease-in-out ${
                            column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                        }`}
                    >
                        <ArrowDownIconSolid />
                    </div>
                </Button>
            ),
            cell: ({ row }) => (
                <div className="text-sm text-popover-800 truncate">{row.getValue('eventType')}</div>
            ),
        },
        {
            accessorKey: 'userName',
            header: ({ column }) => (
                <Button
                    variant="link"
                    className="h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline"
                    onClick={() => handleSort('User', column)}
                >
                    {t('main.superAdmin.userActivities.table.header.userName')}
                    <div
                        className={`transition-transform duration-50 ease-in-out ${
                            column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                        }`}
                    >
                        <ArrowDownIconSolid />
                    </div>
                </Button>
            ),
            cell: ({ row }) => (
                <div className="text-sm text-popover-800 truncate">{row.getValue('userName')}</div>
            ),
        },
        {
            id: 'assignedTenants',
            header: () => (
                <span className="text-sm text-popover-1000 font-medium">
                    {t('main.superAdmin.userActivities.table.header.tenants')}
                </span>
            ),
            cell: ({ row }) => {
                const list = row.original.assignedTenants ?? [];
                const text = list
                    .map((x) => (x.name?.trim() ? x.name : x.tenantId))
                    .join(', ');
                return (
                    <div className="text-sm text-popover-800 max-w-xs truncate" title={text || undefined}>
                        {text || '—'}
                    </div>
                );
            },
        },
        {
            accessorKey: 'details',
            header: () => (
                <span className="text-sm text-popover-1000 font-medium">
                    {t('main.superAdmin.userActivities.table.header.details')}
                </span>
            ),
            cell: ({ row }) => {
                const details = row.getValue('details') as string | null | undefined;
                return (
                    <div className="text-sm text-popover-800 max-w-xs truncate" title={details || undefined}>
                        {details || '—'}
                    </div>
                );
            },
        },
    ];
};

export default useUserActivitiesPageColumns;
